import { NostrEvent } from '@nostr/tools/core'
import { getKindDescription } from '@/lib/nostr-kinds-registry'
import { cn } from '@/lib/utils'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { useAtomValue } from 'jotai'
import * as kinds from '@nostr/tools/kinds'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ChatMessagePreview from './ChatMessagePreview'
import CodeSnippetPreview from './CodeSnippetPreview'
import SlashCommandDefinitionPreview from './SlashCommandDefinitionPreview'
import ForumTopicPreview from './ForumTopicPreview'
import GeocacheCurationListPreview from './GeocacheCurationListPreview'
import GeocacheFoundLogPreview from './GeocacheFoundLogPreview'
import GeocacheListingPreview from './GeocacheListingPreview'
import { GEOCACHE_CURATION_LIST, GEOCACHE_LISTING } from '../Note/GeocacheListing'
import GeocacheVerificationPreview from './GeocacheVerificationPreview'
import GitEventPreview from './GitEventPreview'
import GroupMetadataPreview from './GroupMetadataPreview'
import HighlightPreview from './HighlightPreview'
import LiveEventPreview from './LiveEventPreview'
import LongFormArticlePreview from './LongFormArticlePreview'
import MergeRequestPreview from './MergeRequestPreview'
import NormalContentPreview from './NormalContentPreview'
import {
  BadgeAwardPreview,
  BadgeDefinitionPreview,
  BadgeSetPreview,
  ProfileBadgesPreview
} from './BadgePreview'
import PictureNotePreview from './PictureNotePreview'
import PodcastEpisodePreview from './PodcastEpisodePreview'
import PodcastMetadataPreview from './PodcastMetadataPreview'
import PollPreview from './PollPreview'
import ReactionPreview from './ReactionPreview'
import TorrentPreview from './TorrentPreview'
import VideoNotePreview from './VideoNotePreview'
import WebBookmarkPreview from './WebBookmarkPreview'
import WikiArticlePreview from './WikiArticlePreview'
import WikiRedirectPreview from './WikiRedirectPreview'
import { DEPRECATED_PROFILE_BADGES_D, PROFILE_BADGES_KIND } from '../Note/badge-utils'
import { SLASH_COMMAND_DEFINITION_KIND } from '../../lib/slash-command-definition'
import { tagNameEquals } from '@/lib/tag'

export default function ContentPreview({
  event,
  className
}: {
  event?: NostrEvent
  className?: string
}) {
  const { t } = useTranslation()
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const [kindDescription, setKindDescription] = useState(event ? `Kind ${event.kind}` : '')
  const isMuted = useMemo(
    () => (event ? mutePubkeySet.has(event.pubkey) : false),
    [mutePubkeySet, event]
  )
  useEffect(() => {
    if (!event) {
      return
    }
    let isActive = true
    setKindDescription(`Kind ${event.kind}`)
    getKindDescription(event.kind)
      .then((description) => {
        if (isActive) {
          setKindDescription(description)
        }
      })
      .catch(() => undefined)
    return () => {
      isActive = false
    }
  }, [event])

  if (!event) {
    return (
      <span className={cn('pointer-events-none', className)}>{`[${t('Note not found')}]`}</span>
    )
  }

  if (isMuted) {
    return (
      <div className={cn('pointer-events-none', className)}>[{t('This user has been muted')}]</div>
    )
  }

  switch (event.kind) {
    case kinds.ShortTextNote:
    case kinds.Comment:
    case kinds.Voice:
    case kinds.VoiceComment:
      return <NormalContentPreview event={event} className={className} />

    case kinds.ChatMessage:
      return <ChatMessagePreview event={event} className={className} />

    case SLASH_COMMAND_DEFINITION_KIND:
      return <SlashCommandDefinitionPreview event={event} className={className} />

    case kinds.ForumThread:
      return <ForumTopicPreview event={event} className={className} />

    case kinds.CodeSnippet:
      return <CodeSnippetPreview event={event} className={className} />

    case kinds.PodcastMetadata:
      return <PodcastMetadataPreview event={event} className={className} />

    case kinds.PodcastEpisode:
      return <PodcastEpisodePreview event={event} className={className} />

    case kinds.WikiArticle:
      return <WikiArticlePreview event={event} className={className} />

    case kinds.Redirects:
      return <WikiRedirectPreview event={event} className={className} />

    case kinds.MergeRequests:
      return <MergeRequestPreview event={event} className={className} />

    case GEOCACHE_LISTING:
      return <GeocacheListingPreview event={event} className={className} />

    case kinds.GeocacheLog:
      return <GeocacheFoundLogPreview event={event} className={className} />

    case kinds.GeocacheProofOfFind:
      return <GeocacheVerificationPreview event={event} className={className} />

    case GEOCACHE_CURATION_LIST:
      return <GeocacheCurationListPreview event={event} className={className} />

    case kinds.Patch:
    case kinds.GitPullRequest:
    case kinds.GitPullRequestUpdate:
    case kinds.Issue:
    case kinds.StatusOpen:
    case kinds.StatusApplied:
    case kinds.StatusClosed:
    case kinds.StatusDraft:
    case kinds.UserGraspList:
    case kinds.RepositoryAnnouncement:
    case kinds.RepositoryState:
      return <GitEventPreview event={event} className={className} />

    case kinds.Reaction:
      return <ReactionPreview event={event} className={className} />

    case kinds.BadgeAward:
      return <BadgeAwardPreview event={event} className={className} />

    case kinds.BadgeDefinition:
      return <BadgeDefinitionPreview event={event} className={className} />

    case PROFILE_BADGES_KIND:
      return <ProfileBadgesPreview event={event} className={className} />

    case kinds.ProfileBadges:
      // kind 30008: deprecated profile badges vs badge set
      return event.tags.find(tagNameEquals('d'))?.[1] === DEPRECATED_PROFILE_BADGES_D ? (
        <ProfileBadgesPreview event={event} className={className} />
      ) : (
        <BadgeSetPreview event={event} className={className} />
      )

    case kinds.Highlights:
      return <HighlightPreview event={event} className={className} />

    case kinds.Poll:
      return <PollPreview event={event} className={className} />

    case kinds.LongFormArticle:
      return <LongFormArticlePreview event={event} className={className} />

    case kinds.NormalVideo || event.kind === kinds.ShortVideo:
      return <VideoNotePreview event={event} className={className} />

    case kinds.Photo:
      return <PictureNotePreview event={event} className={className} />

    case kinds.GroupMetadata:
      return <GroupMetadataPreview event={event} className={className} />

    case kinds.WebBookmarks:
      return <WebBookmarkPreview event={event} className={className} />

    case kinds.Torrent:
      return <TorrentPreview event={event} className={className} />

    case kinds.LiveEvent:
      return <LiveEventPreview event={event} className={className} />
  }

  return <div className={className}>[{kindDescription || `Kind ${event.kind}`}]</div>
}
